// Convert an uploaded file to markdown. Mirrors the legacy `fileToMarkdown`
// helper from web/public/app.js — TXT/PDF/DOCX are parsed in the browser when
// possible; audio/image/scanned PDFs fall back to the server's
// `/api/document-to-markdown` endpoint.

import mammoth from "mammoth/mammoth.browser";

const AUDIO_EXTS = ["mp3", "wav", "m4a", "ogg", "webm", "flac"];
const IMAGE_EXTS = ["png", "jpg", "jpeg", "webp"];
const MIN_PDF_TEXT_LENGTH = 200;

export const ACCEPTED_FILE_EXTS = [
  "pdf",
  "docx",
  "txt",
  ...AUDIO_EXTS,
  ...IMAGE_EXTS,
];

export const ACCEPT_ATTR = ACCEPTED_FILE_EXTS.map((e) => "." + e).join(",");

export function fileExt(name: string): string {
  return (name.split(".").pop() || "").toLowerCase();
}

export function uploadStatusLabel(ext: string): string {
  if (AUDIO_EXTS.includes(ext)) return "Wird transkribiert…";
  if (IMAGE_EXTS.includes(ext)) return "Wird analysiert…";
  return "Wird konvertiert…";
}

async function uploadToServer(file: File): Promise<string> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch("/api/document-to-markdown", { method: "POST", body: fd, credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText || "Konvertierung fehlgeschlagen");
  }
  const data = (await res.json()) as { markdown?: string };
  return data.markdown ?? "";
}

async function pdfToText(file: File): Promise<string> {
  const pdfjs = await import("pdfjs-dist");
  // pdfjs ships its worker as a separate file. Vite resolves the worker URL
  // via its `?url` import suffix and pdfjs picks it up at runtime.
  const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  const parts: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const text = content.items.map((it) => ("str" in it ? it.str : "")).join(" ");
    if (text.trim()) parts.push(text.trim());
  }
  return parts.join("\n\n");
}

export async function fileToMarkdown(file: File): Promise<string> {
  const ext = fileExt(file.name);

  if (ext === "txt") {
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === "string" ? r.result : "");
      r.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
      r.readAsText(file, "UTF-8");
    });
  }

  if (ext === "pdf") {
    const text = await pdfToText(file);
    if (text.trim().length >= MIN_PDF_TEXT_LENGTH) return text.trim();
    // Fall back to OCR/server when the PDF has too little embedded text
    // (likely scanned).
    return await uploadToServer(file);
  }

  if (ext === "docx") {
    const arr = await file.arrayBuffer();
    const result = await mammoth.convertToMarkdown({ arrayBuffer: arr });
    return result.value || "";
  }

  if (AUDIO_EXTS.includes(ext) || IMAGE_EXTS.includes(ext)) {
    return await uploadToServer(file);
  }

  throw new Error("Dateityp nicht unterstützt: " + ext);
}
